/**
 * problem+json handling for the purchasing screens.
 *
 * The house convention (SPEC-CONVENTIONS + @/components/form) is: field-level
 * `errors[]` land on the fields, global problems raise a toast, 412 opens the
 * conflict dialog. These screens edit dynamic line arrays outside a single
 * react-hook-form tree, so they collect `errors[]` by wire field path
 * (`lines[2].qty_ordered`) and render them next to the matching control;
 * `surfaceProblem` still owns every global surface.
 */
import type { TFunction } from "i18next";

import { ApiError } from "@/lib/api/errors";
import { isPreconditionFailed } from "@/features/shared/ops-api";
import { surfaceProblem } from "@/components/form";

/** Wire field path → message, e.g. `{ "lines[2].qty_ordered": "…" }`. */
export type FieldErrors = Record<string, string>;

export function collectFieldErrors(error: unknown): FieldErrors {
  const problem = error instanceof ApiError ? error.problem : undefined;
  const collected: FieldErrors = {};
  for (const fieldError of problem?.errors ?? []) {
    // First error per field wins, matching RHF's criteria mode.
    collected[fieldError.field] ??= fieldError.message;
  }
  return collected;
}

/** Stable machine code of a problem, when the server sent one. */
export function problemCode(error: unknown): string | undefined {
  return error instanceof ApiError ? error.code : undefined;
}

export interface HandleProblemOptions {
  /** Called instead of the toast on a 412 optimistic-concurrency conflict. */
  onConflict?: () => void;
  /** Receives the collected per-field messages (may be empty). */
  onFieldErrors?: (errors: FieldErrors) => void;
  /**
   * Return true to claim the problem — the screen renders it itself and no
   * toast is raised (used for typed codes with dedicated inline UX).
   */
  onCode?: (code: string, error: ApiError) => boolean;
}

/**
 * The one entry point every purchasing mutation's `onError` calls.
 * Returns the field errors it collected so callers can also inspect them.
 */
export function handleProblem(
  error: unknown,
  t: TFunction,
  options: HandleProblemOptions = {},
): FieldErrors {
  const fieldErrors = collectFieldErrors(error);
  options.onFieldErrors?.(fieldErrors);

  if (isPreconditionFailed(error) && options.onConflict !== undefined) {
    options.onConflict();
    return fieldErrors;
  }

  const code = problemCode(error);
  if (code !== undefined && error instanceof ApiError && options.onCode?.(code, error) === true) {
    return fieldErrors;
  }

  // surfaceProblem stays silent for field-level problems and toasts the rest.
  surfaceProblem(error, t);
  return fieldErrors;
}
